import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { ALL_TSP_FUNDS } from "@/lib/finance";
import { LINE_COLORS } from "@/components/FundLinesChart";

const MAX = 8;
const label = (f) => (f.length === 1 ? `${f} Fund` : f);

function FundPicker({ funds, onSave }) {
  const [sel, setSel] = useState(funds);
  const latest = useFetch("/prices/latest");
  useEffect(() => setSel(funds), [funds]);
  const end = latest.data?.prices?.[0]?.date;
  const savedKey = funds.join(",");
  const toggle = (f) => setSel((s) => (s.includes(f) ? s.filter((x) => x !== f) : s.length >= MAX ? s : [...s, f]));
  const src = end && sel.length ? `${process.env.REACT_APP_BACKEND_URL}/api/charts/weekly.png?end=${end}&funds=${encodeURIComponent(sel.join(","))}` : null;
  return (
    <div className="mt-5 pt-5 border-t border-border grid lg:grid-cols-[1fr_360px] gap-5" data-testid="email-fund-picker">
      <div>
        <p className="text-sm font-medium">Funds in your email chart <span className="text-xs text-muted-foreground font-normal">({sel.length}/{MAX})</span></p>
        <div className="flex flex-wrap gap-2 mt-3">
          {ALL_TSP_FUNDS.map((f) => {
            const on = sel.includes(f);
            return (
              <button key={f} type="button" onClick={() => toggle(f)} disabled={!on && sel.length >= MAX}
                className={`text-xs rounded-full border px-3 py-1 inline-flex items-center gap-1.5 transition-colors disabled:opacity-40 ${on ? "border-slate-900 bg-slate-900 text-white dark:border-cyan-400 dark:bg-cyan-400/15 dark:text-cyan-200" : "border-border hover:bg-slate-900/5 dark:hover:bg-white/5"}`}
                data-testid={`email-fund-${f.replace(" ", "-")}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: LINE_COLORS[f] }} />{label(f)}
              </button>
            );
          })}
        </div>
        <div className="flex gap-2 mt-4">
          <Button size="sm" disabled={!sel.length || sel.join(",") === savedKey} onClick={() => onSave(sel)} data-testid="email-funds-save">Save chart funds</Button>
          <Button size="sm" variant="ghost" onClick={() => setSel(["C", "S"].concat(sel.filter((f) => f.startsWith("L ")).slice(0, 1)))} data-testid="email-funds-cs-preset">C + S + my L Fund</Button>
        </div>
      </div>
      <div>
        <p className="text-xs text-muted-foreground mb-2">Preview of this week's chart</p>
        {src ? <img src={src} alt="Weekly chart preview" className="w-full rounded-md border border-border bg-white" data-testid="email-chart-preview" /> : <p className="text-xs text-muted-foreground">Pick at least one fund.</p>}
      </div>
    </div>
  );
}

export function WeeklyEmailToggle({ compactCard = false }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    if (user) api.get("/email/preferences").then((r) => setPrefs(r.data)).catch(() => {});
  }, [user]);
  const update = async (patch, msg) => {
    const prev = prefs;
    setPrefs({ ...prefs, ...patch });
    try {
      const r = await api.put("/email/preferences", patch);
      setPrefs((p) => ({ ...p, ...r.data }));
      toast.success(msg);
    } catch (e) { setPrefs(prev); toast.error(errMsg(e)); }
  };
  const preview = async () => {
    try { await api.post("/email/weekly/preview"); toast.success(`Preview sent to ${user.email}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const on = !!prefs?.weekly_recap;
  return (
    <div className={`glass p-5 ${compactCard ? "" : "mb-8"} fade-up`} data-testid="weekly-email-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex gap-3">
          <EnvelopeSimple size={22} className="text-cyan-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-slate-900 dark:text-white">Weekly TSP recap email</p>
            <p className="text-sm text-muted-foreground">A short Saturday email with each fund's weekly move, a chart of the funds you pick, and links to the daily recaps.</p>
          </div>
        </div>
        {user ? (
          <div className="flex items-center gap-3">
            {user.role === "admin" && <Button size="sm" variant="ghost" onClick={preview} data-testid="weekly-email-preview-button">Send me a preview</Button>}
            <Switch checked={on} disabled={!prefs} onCheckedChange={(v) => update({ weekly_recap: v }, v ? `Weekly recap will be sent to ${user.email}` : "Weekly recap turned off")} data-testid="weekly-email-switch" />
          </div>
        ) : (
          <Link to="/login" className="text-sm font-medium text-cyan-700 dark:text-cyan-400 underline" data-testid="weekly-email-signin-link">Sign in to subscribe</Link>
        )}
      </div>
      {user && on && prefs?.email_funds && <FundPicker funds={prefs.email_funds} onSave={(f) => update({ email_funds: f }, "Email chart funds saved")} />}
    </div>
  );
}
